import { downloadByLinkCreation } from './downloadByLinkCreation';
import { DcAppParentMapping } from '../../modules/mac/duck/api';

const formatISODateToDefault = (date: string | undefined) =>
  date ? date.split('T', 1)[0].replace('/', '-') : '';

const csvFormatter = (value) =>
  value === null || value === '' ? '""' : `"${String(value).replace(/"/g, "'")}"`;

const convertLicensesToCSVDefault = (data: any, partnerId: string) => {
  let str =
    'SEN, Product, Organization, Main Contact, Start Date, End Date, Last Transaction Date, Entitlement Id, Entitlement Number\r\n';

  data.forEach((item) => {
    const isMainContact = item.partnerId?.toString() === partnerId;
    const mainContact = isMainContact && item.mainContactEmail;

    const startDate = isMainContact && formatISODateToDefault(item.startDate);
    const endDate = isMainContact && formatISODateToDefault(item.expireDate);
    const lastTransactionDate = isMainContact && formatISODateToDefault(item.lastTransactionDate);

    const csvRow = `${csvFormatter(item.accountId)},${csvFormatter(
      item.productDescription,
    )},${csvFormatter(item.accountName)},${csvFormatter(mainContact)},${csvFormatter(
      startDate,
    )},${csvFormatter(endDate)},${csvFormatter(lastTransactionDate)},${csvFormatter(
      item.ccpEntitlementId,
    )},${csvFormatter(item.slug)}`;
    str += `${csvRow}\r\n`;
  });
  return str;
};

const convertLicensesToCSVMigration = (data: any) => {
  let str =
    'organisation, sen, name, site_or_org_id, main_technical_contact, product_sen, product, migration_date, entitlement_id, entitlement_number\r\n';

  data.forEach((item) => {
    const sen = item.parentAccountId && `SEN-${item.parentAccountId}`;
    const migrationDate = formatISODateToDefault(item.migrationDate);

    const csvRow = `${csvFormatter(item.mainContactOrganisationName)},${csvFormatter(
      sen,
    )},${csvFormatter(item.accountName)},${csvFormatter(item.externalUuid)},${csvFormatter(
      item.mainContactEmail,
    )},${csvFormatter(item.accountId)},${csvFormatter(item.productDescription)},${csvFormatter(
      migrationDate,
    )},${csvFormatter(item.ccpEntitlementId)},${csvFormatter(item.slug)}`;
    str += `${csvRow}\r\n`;
  });
  return str;
};

const convertProductOfferingKeyMappingToCSV = (data: any) => {
  let str =
    'old_product_key, old_external_sku_id, new_product_id, new_offering_id, description, vendor_name, publicly_available\n';

  data.forEach((item) => {
    const publiclyAvailable = item.publicly_available ? 'YES' : 'NO';

    const csvRow = `${csvFormatter(item.old_product_key)},${csvFormatter(
      item.old_external_sku_id,
    )},${csvFormatter(item.new_product_id)},${csvFormatter(item.new_offering_id)},${csvFormatter(
      item.description,
    )},${csvFormatter(item.vendor_name)},${publiclyAvailable}`;
    str += `${csvRow}\n`;
  });
  return str;
};

const downloadCSV = (csvData: any, filename: string) => {
  if (!csvData) return;

  const blob = new Blob([csvData], { type: 'text/csv' });
  const href = URL.createObjectURL(blob);
  const download = `${filename}.csv`;

  downloadByLinkCreation(href, download);
};

/**
 * Creates a link element with the file content as a base64 encoded string and the suggested filename.
 * Clicks the link to download the file and removes the link from the DOM.
 * @param response The response of fetched api.
 * @param isMigration The type of CSV to download.
 * @param partnerId The partner Id to check if it's main contact for CSV download.
 * @returns void
 */

export const downloadLicensesOverviewCSV = (
  response: any,
  isMigration: boolean,
  partnerId: string,
) => {
  if (!response) return;

  const convertToCSV = isMigration ? convertLicensesToCSVMigration : convertLicensesToCSVDefault;
  const filename = isMigration ? 'Migration overview' : 'Customer account overview';
  const csvData = convertToCSV(response, partnerId);
  downloadCSV(csvData, filename);
};

export const downloadProductOfferingKeyMappingsCSV = (response: any) => {
  if (!response) return;

  const csvData = convertProductOfferingKeyMappingToCSV(response);
  downloadCSV(csvData, 'product-offering-key-mappings');
};

/**
 * Converts DC App Parent Mapping data to CSV format and downloads it
 * CSV headers: app_account_id, parent_account_id
 * @param data - Array of DC App Parent Mapping objects
 * @param partnerId - The partner ID (used in filename)
 * @returns void
 */
const convertDcAppParentMappingToCSV = (data: DcAppParentMapping[]) => {
  let str = 'app_account_id,parent_account_id\r\n';

  data.forEach((item) => {
    const csvRow = `${csvFormatter(item.appAccountId)},${csvFormatter(item.parentAccountId)}`;
    str += `${csvRow}\r\n`;
  });

  return str;
};

export const downloadDcAppParentMappingCSV = (
  response: DcAppParentMapping[],
  partnerId: string,
) => {
  if (!response || !Array.isArray(response)) return;

  const csvData = convertDcAppParentMappingToCSV(response);
  const filename = `dc-app-parent-mapping-partner-${partnerId}`;
  downloadCSV(csvData, filename);
};
